import { DateTime } from 'luxon';

import { Button } from '@/components/Button';
import { Sheet } from '@/components/Sheet';
import { formatDuration } from '@/features/tasks/tree';

export interface ConflictChoice {
  mode: 'overlap' | 'split';
}

interface Props {
  taskTitle: string;
  startsAt: Date;
  endsAt: Date;
  timezone: string;
  canSplit: boolean;
  onClose: () => void;
  onChoose: (choice: ConflictChoice) => void;
}

/**
 * Shown when a block would overlap busy time.
 *
 * Overlap keeps one block; split pauses before the conflict and resumes in the
 * next free slot: still one task in the backlog.
 */
export function ConflictChoiceSheet({
  taskTitle,
  startsAt,
  endsAt,
  timezone,
  canSplit,
  onClose,
  onChoose,
}: Props) {
  const durationMin = Math.round((endsAt.getTime() - startsAt.getTime()) / 60_000);
  const range = `${DateTime.fromJSDate(startsAt, { zone: timezone }).toFormat('h:mm a')} – ${DateTime.fromJSDate(endsAt, { zone: timezone }).toFormat('h:mm a')}`;

  return (
    <Sheet
      title="Busy time in the way"
      onClose={onClose}
      footer={
        <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="secondary" onClick={() => onChoose({ mode: 'overlap' })}>
            Overlap
          </Button>
          {canSplit && (
            <Button onClick={() => onChoose({ mode: 'split' })}>
              Split around it
            </Button>
          )}
        </div>
      }
    >
      <p className="text-body text-text">
        <span className="font-medium">{taskTitle}</span> ({range},{' '}
        {formatDuration(durationMin) ?? `${durationMin}m`}) overlaps something already on
        your calendar.
      </p>
      <ul className="mt-4 space-y-2 text-small text-text-secondary">
        <li>
          <span className="font-medium text-text">Overlap</span>: keep one block on top of
          the busy time.
        </li>
        {canSplit && (
          <li>
            <span className="font-medium text-text">Split around it</span>: work until the
            conflict, then continue as <span className="text-text">cont {taskTitle}</span>{' '}
            when there is free time. Still one task, not two in the backlog.
          </li>
        )}
      </ul>
      {!canSplit && (
        <p className="mt-4 text-small text-text-tertiary">
          There is not enough free time nearby to split this block automatically.
        </p>
      )}
    </Sheet>
  );
}
